import { useEffect, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ProgressBar } from "@/components/ProgressBar";
import { FieldError } from "@/components/FieldError";
import { NavButtons } from "@/components/NavButtons";
import { QuestionShell } from "@/components/QuestionShell";
import { StepRenderer } from "@/components/steps/StepRenderer";
import { track } from "@/analytics/analytics";
import type { IntakeRuntimeConfig } from "@/types/embed";
import type { OfferConfig } from "@/types/offer";
import { StatusScreen } from "@/features/status/StatusScreen";
import { IntakeProvider } from "./IntakeContext";
import { ConfirmationScreen } from "./ConfirmationScreen";
import { useIntakeMachine } from "./useIntakeMachine";

interface IntakeFlowProps {
  offer: OfferConfig;
  runtime: IntakeRuntimeConfig;
}

export function IntakeFlow({ offer, runtime }: IntakeFlowProps) {
  const navigate = useNavigate();
  const machine = useIntakeMachine(offer, runtime);
  const { step, stepIndex, totalSteps, progress, phase } = machine;

  useEffect(() => {
    track("question_viewed", { stepId: step.id, type: step.type, index: stepIndex + 1 });
  }, [step.id, step.type, stepIndex]);

  useEffect(() => {
    if (phase !== "submitted" || runtime.mode !== "standalone" || !machine.reportId) return;
    navigate(`/${runtime.offerId}/preview/${machine.reportId}`, { replace: true });
  }, [phase, runtime.mode, runtime.offerId, machine.reportId, navigate]);

  if (phase === "submitted") {
    // Embedded: show live status/progress + preview inline (the host page can't navigate to the
    // standalone status route). Standalone navigates to the preview route via the effect above.
    if (runtime.mode === "embedded" && machine.reportId) {
      return (
        <IntakeProvider value={machine}>
          <StatusScreen
            reportId={machine.reportId}
            offerId={runtime.offerId}
            embedded
            appBaseUrl={runtime.appBaseUrl}
          />
        </IntakeProvider>
      );
    }

    return (
      <IntakeProvider value={machine}>
        <div className="mx-auto w-full max-w-2xl px-5 py-12">
          <ConfirmationScreen
            reportId={machine.reportId}
            email={machine.email}
            offerId={runtime.offerId ?? ""}
            mode={runtime.mode}
          />
        </div>
      </IntakeProvider>
    );
  }

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    const target = e.target as HTMLElement;
    if (target.tagName === "TEXTAREA") return;
    if (step.type === "review" || step.type === "fileUpload") return;
    e.preventDefault();
    machine.next();
  };

  const isReview = step.type === "review";
  const nextLabel = step.type === "statement" ? step.ctaLabel ?? "Continue" : "Continue";

  return (
    <IntakeProvider value={machine}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="flex min-h-full flex-col" onKeyDown={onKeyDown}>
        <div className="mx-auto w-full max-w-2xl px-5 pt-6">
          <ProgressBar value={progress} current={stepIndex + 1} total={totalSteps} />
        </div>

        <div className="mx-auto flex w-full max-w-2xl flex-1 items-start px-5 py-8 sm:py-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              className="w-full"
            >
              <QuestionShell
                stepId={step.id}
                index={stepIndex + 1}
                title={step.title}
                description={step.description}
                helpText={step.helpText}
                required={step.required}
              >
                <StepRenderer
                  step={step}
                  index={stepIndex + 1}
                  value={machine.answers[step.id]}
                  error={machine.showErrors ? machine.currentError : null}
                  onChange={(v) => machine.setAnswer(step.id, v)}
                  onAdvance={machine.next}
                  autoFocus
                />

                <FieldError message={machine.showErrors ? machine.currentError : null} />

                {phase === "error" && machine.submitError ? (
                  <p role="alert" className="mt-2 text-sm font-medium text-red-600">
                    {machine.submitError}
                  </p>
                ) : null}

                {!isReview ? (
                  <NavButtons
                    onBack={machine.back}
                    onNext={machine.next}
                    canGoBack={stepIndex > 0}
                    canAdvance={machine.canAdvance}
                    nextLabel={nextLabel}
                    showEnterHint={step.type !== "fileUpload"}
                  />
                ) : (
                  <div className="mt-6">
                    {stepIndex > 0 ? (
                      <button type="button" className="r1-btn-ghost" onClick={machine.back}>
                        Back
                      </button>
                    ) : null}
                  </div>
                )}
              </QuestionShell>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </IntakeProvider>
  );
}
